// @flow
import {
    CLEAR_TIMEOUT,
    TIMEOUT_TICK,
    SET_TIMEOUT,
    timerWorkerScript
} from './TimerWorker';
import {i18next} from "../../base/i18n";
import {
    getLocalVideoTrack,
    getTrackByMediaTypeAndParticipant
} from "../../base/tracks";
import {setPictureInPictureEnabled} from "../../picture-in-picture/actions";
import {
    getParticipantById, getParticipantDisplayName,
    getPinnedParticipant, getRaiseHandsQueue, isScreenShareParticipant
} from "../../base/participants";
import {MEDIA_TYPE} from "../../base/media";
import {getSortedParticipantIds} from "../../participants-pane/functions";
import {getAvatarColor, getInitials} from "../../base/avatar";

const AVATAR_COLORS = [
    '#6A50D3',
    '#FF9B42',
    '#DF486F',
    '#73348C',
    '#B23683',
    '#F96E57',
    '#4380E2',
    '#2AA076',
    '#00A8B3'
];

/**
 * Represents a modified MediaStream that adds effects to video background.
 * <tt>JitsiStreamBackgroundEffect</tt> does the processing of the original
 * video stream.
 */
export default class PictureInPicture {
    _pictureInPictureVideoElement: HTMLViRdeoElement;
    _pictureInPictureTimerWorker: Worker;
    _pictureInPictureCanvasElement: HTMLCanvasElement;
    _pictureInPictureCanvasCtx: Object;
    _offScreenCanvasElement: HTMLCanvasElement[];
    _offScreenCanvasCtx: Object[];
    _width: Number;
    _height: Number;
    _radius: Number;
    _fps: Number;
    _pictureInPictureLayout: Function;
    _drawCanvas: Function;
    _drawOffScreenCanvas: Function;
    _drawLayout: Function;
    isEnabled: Function;
    startEffect: Function;
    stopEffect: Function;
    APP: Object;

    /**
     * Represents a modified video MediaStream track.
     *
     * @class
     */
    constructor(dispatch: Function) {
        this._width = 1280;
        this._height = 720;
        this._fps = 30;
        this._radius = 40;
        this._offScreenCanvasElement = Array();
        this._offScreenCanvasCtx = Array();
        this._imageCaptureCache = {};  // key: track.id

        this._onPictureInPictureTimer = this._onPictureInPictureTimer.bind(this);
        this._pictureInPictureLayout = this._pictureInPictureLayout.bind(this);
        this._drawCanvas = this._drawCanvas.bind(this);
        this._drawOffScreenCanvas = this._drawOffScreenCanvas.bind(this);
        this._fillTextOffScreenCanvas =  this._fillTextOffScreenCanvas.bind(this);
        this._drawCircleOffScreenCanvas = this._drawCircleOffScreenCanvas.bind(this);
        this._fillTextParticipantsCounts = this._fillTextParticipantsCounts.bind(this);
        this._drawStrokeCanvas = this._drawStrokeCanvas.bind(this);
        this._drawLayout = this._drawLayout.bind(this);
        this._roundedImage =  this._roundedImage.bind(this);
        this._runPictureInPicture = this._runPictureInPicture.bind(this);
        this.stopEffect = this.stopEffect.bind(this);
        this.getSortedParticipantIds = this.getSortedParticipantIds.bind(this);
        this.getParticipantTrack = this.getParticipantTrack.bind(this);

        this._pictureInPictureCanvasElement = document.createElement('canvas');
        this._pictureInPictureCanvasElement.getContext('2d');

        this._pictureInPictureVideoElement = document.createElement('video');
        this._pictureInPictureVideoElement.addEventListener('leavepictureinpicture', function (event) {
            // Video left Picture-in-Picture.
            // User may have played a Picture-in-Picture video from a different page.
            dispatch(setPictureInPictureEnabled(false));
        });
    }

    /**
     * EventHandler onmessage for the maskFrameTimerWorker WebWorker.
     *
     * @private
     * @param {EventHandler} response - The onmessage EventHandler parameter.
     * @returns {void}
     */
    _onPictureInPictureTimer(response: Object) {
        if (response.data.id === TIMEOUT_TICK) {
            this._runPictureInPicture();
        }
    }

    async _pictureInPictureLayout() {
        const ids = this.getSortedParticipantIds();
        const arrayLength = ids.length;
        const arrayLengthString = arrayLength.toString();
        const raiseHandQueueLength = getRaiseHandsQueue(APP.store.getState()).length.toString();

        this._pictureInPictureCanvasCtx.clearRect(0,0, this._width, this._height);
        await this._drawLayout(ids);
        if (arrayLength === 1) {
            this._drawCanvas(this._offScreenCanvasElement[0], this._width / 12, this._height / 12, 10 * this._width / 12, 10 * this._height / 12);
        } else if (arrayLength === 2) {
            this._drawCanvas(this._offScreenCanvasElement[1], this._width / 12, this._height / 12, 10 * this._width / 12, 10 * this._height / 12);
            this._drawCanvas(this._offScreenCanvasElement[0], 8 * this._width / 12, 8 * this._height / 12, this._width / 4, this._height / 4);
        } else if (arrayLength === 3) {
            this._drawCanvas(this._offScreenCanvasElement[0], 4 * this._width / 12, 7 * this._height / 12, 4 * this._width / 12, 4 * this._height / 12);
            this._drawCanvas(this._offScreenCanvasElement[1], this._width / 12, this._height / 12, 4 * this._width / 12, 4 * this._height / 12);
            this._drawCanvas(this._offScreenCanvasElement[2], 7 * this._width / 12, this._height / 12, 4 * this._width / 12, 4 * this._height / 12);
        } else {
            this._drawCanvas(this._offScreenCanvasElement[0], 7 * this._width / 12, 7 * this._height / 12, this._width / 3, this._height / 3);
            this._drawCanvas(this._offScreenCanvasElement[1], this._width / 12, this._height / 12, this._width / 3, this._height / 3);
            this._drawCanvas(this._offScreenCanvasElement[2], 7 * this._width / 12, this._height / 12, 4 * this._width / 12, 4 * this._height / 12);
            this._drawCanvas(this._offScreenCanvasElement[3], this._width / 12, 7 * this._height / 12, this._width / 3, this._height / 3);
            // TODO: 나머지 참가자
        }
        this._fillTextParticipantsCounts(this._pictureInPictureCanvasCtx, arrayLengthString, this._width/12,0, 2 * this._width/12,this._height/12);
        this._fillTexRaiseHandCounts(this._pictureInPictureCanvasCtx, raiseHandQueueLength, 9 * this._width/12,0,2 * this._width/12,this._height/12);
    }

    _drawCanvas(img, x, y, w, h) {
        this._pictureInPictureCanvasCtx.save();
        this._pictureInPictureCanvasCtx.scale(-1, 1);
        this._roundedImage(this._pictureInPictureCanvasCtx, -x-w, y, w, h, this._radius);
        // this._pictureInPictureCanvasCtx.strokeStyle = "red";
        // this._pictureInPictureCanvasCtx.lineWidth = 10;
        // this._pictureInPictureCanvasCtx.stroke();
        this._pictureInPictureCanvasCtx.clip();
        this._pictureInPictureCanvasCtx.drawImage(img, -x-w, y, w, h);
        this._pictureInPictureCanvasCtx.restore();
    }

    _drawOffScreenCanvas(ctx, img, x, y, w, h) {
        ctx.save();
        ctx.drawImage(img, x, y, w, h);
        ctx.restore();
    }

    _drawStrokeCanvas() {
        this._pictureInPictureCanvasCtx.strokeStyle = "red";
        this._pictureInPictureCanvasCtx.lineWidth = 10;
        this._pictureInPictureCanvasCtx.stroke();
    }

    _fillTextOffScreenCanvas(ctx, text, x, y, w, h) {
        ctx.save();
        // ctx.clearRect(0, 0, w, h);
        ctx.scale(-1, 1);
        ctx.font = "280px Arial";
        ctx.fillStyle = "#fff";
        ctx.strokeStyle = "#000";
        ctx.textBaseline = "middle";
        ctx.fillText(text, (- x - w) / 2 - ctx.measureText(text).width/2, h / 2 + h * 0.03);
        // ctx.fillText(text, (- x - w) / 2 - ctx.measureText(text).width/2, (h / 2) + ctx.measureText(text).height/2);
        ctx.restore();
    }

    _drawCircleOffScreenCanvas(ctx, initials, radius, x, y, w, h, startAngle, endAngle) {
        const circleColor = getAvatarColor(initials, AVATAR_COLORS);
        ctx.save();
        ctx.clearRect(0, 0, w, h);
        ctx.scale(-1, 1);
        ctx.beginPath();
        ctx.arc((- x - w) / 2, h/2, radius, startAngle, endAngle);
        ctx.stroke();
        ctx.fillStyle = circleColor;
        ctx.fill();
        // ctx.strokeStyle = "#000";
        ctx.restore();
    }


    _fillTextParticipantsCounts(ctx, text, x, y, w, h) {
        // const ids = this.getSortedParticipantIds();
        // const arrayLength = ids.length.toString();
        ctx.save();
        // ctx.scale(-1, 1);
        ctx.font = "40px sans-serif";
        ctx.fillStyle = "#fff";
        ctx.strokeStyle = "#000";
        ctx.textBaseline = "middle";
        ctx.fillText('👥 ' + text, x, h / 2);
        ctx.restore();
    }


    _fillTexRaiseHandCounts(ctx, text, x, y, w, h) {
        // const ids = this.getSortedParticipantIds();
        // const arrayLength = ids.length.toString();
        ctx.save();
        // ctx.scale(-1, 1);
        ctx.font = "40px sans-serif";
        ctx.fillStyle = "#fff";
        ctx.strokeStyle = "#000";
        ctx.textBaseline = "middle";
        ctx.fillText('✋ ' + text, x, h / 2);
        ctx.restore();
    }


    _roundedImage(ctx, x, y, width, height, radius) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        // ctx.strokeStyle = "red";
        // ctx.lineWidth = 10;
        // ctx.stroke();
    }

    async _drawLayout(ids) {
        for (let i = 0; i < ids.length; i++) {
            const state = APP.store.getState();
            const track = this.getParticipantTrack(ids[i]);

            if(track ===  null || track.kind !=='video'){
                const displayName1 = getParticipantDisplayName(state, ids[i]);
                const initials = getInitials(displayName1); // 이니셜 가져옴 !!!!!

                this._drawCircleOffScreenCanvas(
                    this._offScreenCanvasCtx[i], initials,
                    300, 0, 0, this._width, this._height, 0, 2 * Math.PI);
                this._fillTextOffScreenCanvas(this._offScreenCanvasCtx[i], initials, 0, 0, this._width, this._height);
            } else {
                const trackId = track.id;
                if (!this._imageCaptureCache[trackId]) {
                    try {
                        this._imageCaptureCache[trackId] = new ImageCapture(track);
                    } catch (e) {
                        console.warn("ImageCapture init failed:", e);
                        continue;
                    }
                }

                const imageCapture = this._imageCaptureCache[trackId];

                if(imageCapture && typeof imageCapture.grabFrame === 'function'){
                    //https://developer.mozilla.org/en-US/docs/Web/API/ImageCapture/grabFrame#browser_compatibility
                    imageCapture
                        .grabFrame()
                        .then((img) => {
                            this._drawOffScreenCanvas(this._offScreenCanvasCtx[i], img, 0, 0, this._width, this._height);
                        })
                        .catch((error) => {
                            if (error !== undefined) {
                                const displayName2 = getParticipantDisplayName(state, ids[i]);
                                const initials = getInitials(displayName2); // 이니셜 가져옴 !!!!!

                                this._drawCircleOffScreenCanvas(
                                    this._offScreenCanvasCtx[i], initials,
                                    300, 0, 0, this._width, this._height, 0, 2 * Math.PI);
                                this._fillTextOffScreenCanvas(this._offScreenCanvasCtx[i], initials, 0, 0, this._width, this._height);
                            }
                        });
                }


            }

        }
    }
    /**
     * Loop function to render the background mask.
     *
     * @private
     * @returns {void}
     */
    async _runPictureInPicture() {
        this._pictureInPictureLayout();

        this._pictureInPictureTimerWorker.postMessage({
            id: SET_TIMEOUT,
            timeMs: 1000 / this._fps
        });
    }


    /**
     * Checks if the local track supports this effect.
     *
     * @param {JitsiLocalTrack} jitsiLocalTrack - Track to apply effect.
     * @returns {boolean} - Returns true if this effect can run on the specified track
     * false otherwise.
     */
    isEnabled(jitsiLocalTrack: Object) {
        return jitsiLocalTrack.isVideoTrack() && jitsiLocalTrack.videoType === 'camera';
    }


    /**
     *
     */
    getSortedParticipantIds(){
        const state = APP.store.getState();
        let sortedParticipantIds = getSortedParticipantIds(state);
        sortedParticipantIds = sortedParticipantIds.filter((id: any) => {
            const participant = getParticipantById(state, id);

            return !isScreenShareParticipant(participant);
        });

        return sortedParticipantIds;
    }


    /**
     *
     */
    getParticipantTrack(id){
        const state = APP.store.getState();
        try {
            return getTrackByMediaTypeAndParticipant(state['features/base/tracks'], MEDIA_TYPE.VIDEO, id).jitsiTrack.stream.getVideoTracks()[0];
        } catch (e) {
            return null;
        }
    }


    /**
     * Starts loop to capture video frame and render the segmentation mask.
     */
    startEffect() {

        const isImageCaptureSupported = typeof window.ImageCapture !== 'undefined';

        if (!isImageCaptureSupported) {
            console.warn('Picture-in-Picture is not supported on this browser.');
            return;
        }

        // const ids = this.getSortedParticipantIds();
        // const track = this.getParticipantTrack(ids[0]);
        // const {height, frameRate, width}
        //     = track.getSettings ? track.getSettings() : track.getConstraints();

        // this._width = width;
        // this._height = height;
        // this._fps = frameRate;

        this._pictureInPictureCanvasElement.width = parseInt(this._width, 10);
        this._pictureInPictureCanvasElement.height = parseInt(this._height, 10);
        this._pictureInPictureCanvasCtx = this._pictureInPictureCanvasElement.getContext('2d');
        this._pictureInPictureCanvasCtx.fillRect(0, 0, this._width, this._height);

        for (let i = 0; i < 4; i++) {
            this._offScreenCanvasElement.push(document.createElement('canvas'));
            this._offScreenCanvasElement[i].width = parseInt(this._width, 10);
            this._offScreenCanvasElement[i].height = parseInt(this._height, 10);
            this._offScreenCanvasCtx.push(this._offScreenCanvasElement[i].getContext('2d'));
            this._offScreenCanvasCtx[i].fillRect(0, 0, this._width, this._height);
        }

        this._pictureInPictureVideoElement.width = parseInt(this._width, 10);
        this._pictureInPictureVideoElement.height = parseInt(this._height, 10);
        this._pictureInPictureVideoElement.muted = true;
        this._pictureInPictureVideoElement.srcObject = this._pictureInPictureCanvasElement.captureStream();
        this._pictureInPictureVideoElement.play();
        this._pictureInPictureVideoElement.onloadeddata = () => {
            this._pictureInPictureVideoElement.requestPictureInPicture();
        };

        this._pictureInPictureTimerWorker = new Worker(timerWorkerScript, {name: 'PIP worker'});
        this._pictureInPictureTimerWorker.onmessage = this._onPictureInPictureTimer;
        this._pictureInPictureTimerWorker.postMessage({
            id: SET_TIMEOUT,
            timeMs: 1000 / this._fps
        });
    }

    /**
     * Stops the capture and render loop.
     *
     * @returns {void}
     */
    stopEffect() {
        this._pictureInPictureTimerWorker.postMessage({
            id: CLEAR_TIMEOUT
        });

        this._pictureInPictureTimerWorker.terminate();
        if (this._pictureInPictureVideoElement === document.pictureInPictureElement)
            document.exitPictureInPicture();
    }
}
